import axios from 'axios'
import { attachExpectedSessionUser, reportSessionIdentityChanged } from '../saas/sessionIdentity'
import type {
  Asset,
  EngineInfo,
  Item,
  Job,
  LabelValue,
  ProcessingVersion,
  StatsOverview,
  Workspace,
} from '../types'
import type { BackgroundGradientConfig } from '../components/editor/backgroundPresets'

// Online Preview v0.1 spec §14 — must be configurable via an env var for
// a real deployment where the frontend (e.g. Cloudflare Pages) and API
// (e.g. Render) live on different origins; relative '/api' only works
// when a same-origin reverse proxy (Vite dev proxy / nginx.conf) sits in
// front of both, which is true for local dev and the Docker Compose
// setup but not for static hosting. Never hardcode a real deployment's
// http://localhost:xxxx here — see frontend/.env.example.
//
// Every call site below is a path RELATIVE to this base (e.g. `/chains`,
// never `/api/chains`), matching FastAPI's `app.include_router(api_router,
// prefix="/api")` in backend/app/main.py — so this base must always end in
// `/api`. Deploying with e.g. VITE_API_BASE_URL=https://api.example.com
// (missing the `/api` suffix — a real mistake made once against
// api.vistoera.com) would make every single request 404. Normalize here
// instead of trusting every deployment's env var to be typed exactly
// right — cheap insurance against that entire class of outage.
function withApiSuffix(raw: string): string {
  const trimmed = raw.replace(/\/+$/, '')
  return trimmed.endsWith('/api') ? trimmed : `${trimmed}/api`
}
const RAW_API_BASE_URL = (import.meta.env.VITE_API_BASE_URL as string | undefined)?.trim()
const STANDALONE_BASE_URL = RAW_API_BASE_URL ? withApiSuffix(RAW_API_BASE_URL) : '/api'

const api = axios.create({
  baseURL: STANDALONE_BASE_URL,
  timeout: 120000,
})

// ---------------------------------------------------------------------
// Online Preview v0.1 — Basic Preview Access Protection (spec §19). A
// no-op when the backend's PREVIEW_ACCESS_PASSWORD is unset (the
// header is simply never checked server-side); when it IS set, every
// request must carry it. Stored in localStorage (not a real session —
// this is a single shared preview secret, not a per-user credential).
// ---------------------------------------------------------------------
const PREVIEW_ACCESS_STORAGE_KEY = 'pic2content_preview_access'
// Exported so every other axios instance in this app (currently
// `saas/api.ts`'s `saasApi`) attaches the exact same header the backend's
// `preview_access_gate` middleware reads (`settings.preview_access_header`,
// default `X-Preview-Access` — see `backend/app/core/config.py`) instead of
// each call site inventing its own header name/instance-local copy.
export const PREVIEW_ACCESS_HEADER = 'X-Preview-Access'

export function getStoredPreviewAccessKey(): string | null {
  try {
    return window.localStorage.getItem(PREVIEW_ACCESS_STORAGE_KEY)
  } catch {
    return null
  }
}

export function setStoredPreviewAccessKey(value: string | null): void {
  try {
    if (value) window.localStorage.setItem(PREVIEW_ACCESS_STORAGE_KEY, value)
    else window.localStorage.removeItem(PREVIEW_ACCESS_STORAGE_KEY)
  } catch {
    // localStorage unavailable (e.g. privacy mode) — the gate will just
    // re-prompt on every reload, which is acceptable degradation.
  }
}

// VISTOERA_PREVIEW_DATA_RECOVERY (2026-08-21) — every *raw* `fetch()` call
// site that hits this app's own API (i.e. not `axios`, which every other
// client here already covers via a request interceptor — see
// `saas/api.ts`'s `saasApi`, `saas/public/publicApi.ts`'s `publicApi`,
// `saas/mobileCaptureApi.ts`'s `mobileSaasApi`) must go through this one
// helper instead of re-deriving its own header logic, so a fifth call site
// can never again silently reintroduce the exact "public site 401 on
// Preview only" class of bug this same phase already found and fixed once
// in `publicApi.ts`. No-op (returns `init` unchanged) whenever no key is
// stored — i.e. always a no-op on Local, where the gate is off.
export function withPreviewAccessHeaders(init?: RequestInit): RequestInit {
  const key = getStoredPreviewAccessKey()
  if (!key) return init ?? {}
  return {
    ...init,
    headers: {
      ...(init?.headers ?? {}),
      [PREVIEW_ACCESS_HEADER]: key,
    },
  }
}

export const verifyPreviewAccessKey = (candidate: string) =>
  api.get('/preview-access/verify', { headers: { [PREVIEW_ACCESS_HEADER]: candidate } })

api.interceptors.request.use((config) => {
  const key = getStoredPreviewAccessKey()
  if (key) {
    config.headers = config.headers ?? {}
    ;(config.headers as Record<string, string>)[PREVIEW_ACCESS_HEADER] = key
  }
  // Only the Workspace-scoped (cookie) mode is a session request.
  return config.withCredentials || api.defaults.withCredentials ? attachExpectedSessionUser(config) : config
})

// Dispatched on any 401 whose body carries PREVIEW_ACCESS_REQUIRED, so a
// top-level gate component can re-prompt without every single call site
// needing to know about this concern (mirrors how the response
// interceptor below already normalizes error messages for every caller).
export const PREVIEW_ACCESS_REQUIRED_EVENT = 'pic2content:preview-access-required'

/**
 * Phase 2B-1 — lets the SaaS Tenant Admin "Images" page (`saas/workspace/
 * ImagesPage.tsx`) point this EXACT SAME pipeline client (and therefore
 * every component below, plus `UploadModal`/`ChainEditorModal`/
 * `BackgroundEditorModal`/`ShadowEditorModal`/`RerunTemplateModal`
 * unmodified) at that Workspace's own `/api/saas/workspaces/{id}/images`
 * scope instead of the standalone `/api` root — spec §1/§16: real reuse of
 * the already-working pipeline UI, not a rewritten parallel client.
 *
 * Deliberately a single mutable module-level default rather than a
 * per-call parameter: every one of the ~30 call sites above already takes
 * a fixed relative path, and the SaaS Images page is the only other
 * consumer, mounted on its own route (never simultaneously with the
 * standalone `/` pipeline page in the same tab) — so there is no real
 * concurrent-scope hazard in practice. `ImagesPage` calls this once on
 * mount and restores the standalone scope on unmount so any other still-
 * mounted pipeline UI (e.g. a background tab) is never left pointed at a
 * Workspace it doesn't belong to.
 */
export function setPipelineApiScope(workspaceScopedBaseUrl: string | null): void {
  if (workspaceScopedBaseUrl) {
    api.defaults.baseURL = workspaceScopedBaseUrl
    api.defaults.withCredentials = true
  } else {
    api.defaults.baseURL = STANDALONE_BASE_URL
    api.defaults.withCredentials = false
  }
}

/** Enriched by the response interceptor below with the bits a caller needs
 * to decide whether a failure is worth retrying, without needing the raw
 * (pre-interceptor) axios error: `status` is `undefined` for a true
 * network-level failure (no HTTP response reached the browser at all —
 * e.g. a backend-origin 502 whose generic edge error page carries no CORS
 * headers, which the browser then reports as an opaque "Network Error"
 * with no `response` object, not as a visible 502). */
export type ApiError = Error & {
  status?: number
  retryAfterSeconds?: number
  /** axios failure code (e.g. ECONNABORTED, ERR_CANCELED). */
  code?: string
  /** True when the request was deliberately aborted (never a failure to report). */
  canceled?: boolean
  /** Backend error `detail.code` (e.g. `conflict`, `CIRCULAR_FOLDER_TREE`), when present. */
  detailCode?: string
}

api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err.response?.status === 401 && err.response?.data?.code === 'PREVIEW_ACCESS_REQUIRED') {
      setStoredPreviewAccessKey(null)
      window.dispatchEvent(new Event(PREVIEW_ACCESS_REQUIRED_EVENT))
    }
    const detail = err.response?.data?.detail || err.response?.data?.message || err.message
    // TASK-00533: the standard SaaS error body is `{code, message}` - show the
    // human message (e.g. 同一位置已存在同名文件夹), never the raw JSON.
    const detailMessage =
      detail && typeof detail === 'object' && typeof (detail as { message?: unknown }).message === 'string'
        ? (detail as { message: string }).message
        : null
    const normalized: ApiError = new Error(
      typeof detail === 'string' ? detail : detailMessage ?? JSON.stringify(detail),
    )
    if (detail && typeof detail === 'object' && typeof (detail as { code?: unknown }).code === 'string') {
      normalized.detailCode = (detail as { code: string }).code
    }
    normalized.status = err.response?.status
    reportSessionIdentityChanged(normalized.status, normalized.detailCode)
    // VistoeraTASK-00522: keep the axios failure code + cancel flag so a
    // caller can tell a deliberately aborted request (tab switch, unmount)
    // and a timeout apart from a real network failure.
    normalized.code = typeof err.code === 'string' ? err.code : undefined
    normalized.canceled = axios.isCancel(err)
    const retryAfterHeader = err.response?.headers?.['retry-after']
    const parsedRetryAfter = retryAfterHeader !== undefined ? Number(retryAfterHeader) : NaN
    if (Number.isFinite(parsedRetryAfter)) normalized.retryAfterSeconds = parsedRetryAfter
    return Promise.reject(normalized)
  },
)

/** Transient-failure retry for calls that legitimately need it (currently:
 * Browser Upload, after live cloud testing showed a 502 mid-upload-burst
 * from backend capacity contention — see UploadModal). Deliberately narrow:
 * only retries on signals that mean "the server/edge dropped this one
 * request, try again", never on a real validation/auth 4xx. `status ===
 * undefined` (opaque "Network Error", no HTTP response reached the
 * browser) is treated as retryable too — see `ApiError` above for why that
 * happens on this exact class of failure. */
export function isTransientRetryableError(err: unknown): boolean {
  const status = (err as ApiError | undefined)?.status
  if (status === undefined) return true
  return status === 502 || status === 503 || status === 504 || status === 429
}

export async function withTransientRetry<T>(fn: () => Promise<T>, attempts = 3): Promise<T> {
  let lastErr: unknown
  for (let attempt = 0; attempt < attempts; attempt++) {
    try {
      return await fn()
    } catch (err) {
      lastErr = err
      if (attempt === attempts - 1 || !isTransientRetryableError(err)) throw err
      const retryAfterSeconds = (err as ApiError | undefined)?.retryAfterSeconds
      const delayMs = retryAfterSeconds ? retryAfterSeconds * 1000 : 600 * 2 ** attempt // 600ms, 1.2s, ...
      await new Promise((resolve) => setTimeout(resolve, Math.min(delayMs, 15000)))
    }
  }
  throw lastErr
}

// ---------------------------------------------------------------------
// Resolve-URL contract (Phase R1.5). `/api/media/*` is a single global
// router — never Workspace-rescoped like `api`'s own baseURL can be (see
// `setPipelineApiScope`) — so this deliberately targets `STANDALONE_BASE_URL`
// directly rather than going through the (possibly rescoped) `api`
// instance, mirroring the fact that every `*_url` field a chain payload
// carries (standalone or SaaS) is already always an absolute
// `.../api/media/...` URL for the exact same reason.
// ---------------------------------------------------------------------
const mediaApi = axios.create({ baseURL: STANDALONE_BASE_URL, timeout: 30000 })
mediaApi.interceptors.request.use((config) => {
  const key = getStoredPreviewAccessKey()
  if (key) {
    config.headers = config.headers ?? {}
    ;(config.headers as Record<string, string>)[PREVIEW_ACCESS_HEADER] = key
  }
  return config
})

export type ResolvableMediaKind = 'thumbnail' | 'proxy' | 'mask' | 'transparent' | 'background' | 'shadow'

/**
 * The stable `/api/media/chain/{id}/{kind}` URL (`chain.*_url` fields) is
 * fine for a plain `<img src>`, but 302-redirects to R2 in Cloud mode —
 * which breaks the interactive editors' `img.crossOrigin='anonymous'`
 * pixel loading (a CORS-mode request drops its real `Origin` header on a
 * cross-origin redirect hop per the Fetch spec, so R2 never returns
 * `Access-Control-Allow-Origin` no matter its own CORS config). This calls
 * the backend's resolve endpoint instead, which follows that redirect
 * itself server-side and hands back the final URL as JSON, so the browser
 * only ever does ONE hop with its real Origin header intact.
 *
 * Deliberately re-resolved on every editor open (never persisted/cached) —
 * the returned URL is a short-lived signed R2 URL in Cloud mode.
 */
export const resolveMediaUrl = (chainId: string, kind: ResolvableMediaKind) =>
  mediaApi
    .get<{ url: string }>(`/media/chain/${chainId}/resolve`, { params: { kind }, withCredentials: true })
    .then((r) => r.data.url)

export type PixelKind = ResolvableMediaKind | 'transparent_proxy'

/**
 * Editor Load Performance P0 (2026-08): the static counterpart of
 * `resolveMediaUrl` above. `chain_resolve`'s only remaining server-side job
 * is running the exact same existence/self-heal check
 * (`app/api/media.py::_resolve_storage_key`) that `chain_pixels` (the real
 * byte-serving route below) already runs itself, on every call, independently
 * — see both routes' docstrings. The URL it hands back is therefore no
 * longer a signed/short-lived R2 URL either (that redirect-based design was
 * already replaced by this same-origin `/pixels` proxy so Canvas pixel
 * access never depends on R2's own CORS policy — see `chain_resolve`'s
 * docstring); it is this exact static string, every time. Calling
 * `/resolve` first before every pixel load was therefore a pure extra
 * network round trip (plus duplicate DB self-heal work) for zero benefit —
 * every editor pixel load now builds this URL directly instead.
 */
export const pixelsUrl = (chainId: string, kind: PixelKind) => `${STANDALONE_BASE_URL}/media/chain/${chainId}/${kind}/pixels`

export const health = () => api.get('/health')

export const getDefaultWorkspace = () => api.get<Workspace>('/workspaces/default')

export const listWorkspaces = () => api.get<Workspace[]>('/workspaces')
export const createWorkspace = (data: { name: string; monitored_root_path?: string; scan_enabled?: boolean }) =>
  api.post<Workspace>('/workspaces', data)
export const updateWorkspace = (id: string, data: Partial<Workspace>) =>
  api.patch<Workspace>(`/workspaces/${id}`, data)
export const scanWorkspace = (id: string, folder_path?: string) =>
  api.post(`/workspaces/${id}/scan`, folder_path ? { folder_path } : {})
export const scanStatus = (id: string) => api.get(`/workspaces/${id}/scan-status`)

export const getOverview = (workspaceId?: string) =>
  api.get<StatsOverview>('/dashboard/overview', { params: { workspace_id: workspaceId } })

export const importUpload = (files: File[]) => {
  const form = new FormData()
  files.forEach((f) => form.append('files', f))
  return api.post<{ workspace_id: string; created_count: number; asset_ids: string[]; errors: unknown[] }>(
    '/import/upload',
    form,
    { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 300000 },
  )
}

export const browseFolder = () =>
  api.post<{ cancelled: boolean; path: string | null }>('/import/browse-folder', null, { timeout: 320000 })

export interface QueueItem {
  asset_id: string
  item_id: string
  object_name: string
  object_code: string
  filename: string
  status: string
  status_label: string
  error_message?: string | null
  thumbnail_url: string
  original_url: string
  white_url: string | null
  transparent_url: string | null
  shadow_url: string | null
  is_final: boolean
  latest_version_number: number | null
  created_at: string
  updated_at: string
}

export interface QueueSummary {
  total: number
  pending: number
  processing: number
  waiting_review: number
  completed: number
  failed: number
}

export const getQueue = (params?: { status?: string; limit?: number }) =>
  api.get<QueueItem[]>('/queue', { params })
export const getQueueSummary = () => api.get<QueueSummary>('/queue/summary')

export const listItems = (workspaceId: string, params?: Record<string, string>) =>
  api.get<Item[]>(`/workspaces/${workspaceId}/items`, { params })
export const getItem = (itemId: string) =>
  api.get<{ item: Item; assets: Asset[] }>(`/items/${itemId}`)
export const updateItem = (itemId: string, data: Partial<Item>) =>
  api.patch<Item>(`/items/${itemId}`, data)
export const batchProcess = (data: { item_ids?: string[]; asset_ids?: string[]; engine_name?: string }) =>
  api.post('/items/batch-process', data)
export const exportItem = (itemId: string, opts?: Record<string, unknown>) =>
  api.post(`/items/${itemId}/export`, opts || {})
export const batchExport = (data: { item_ids: string[]; make_zip?: boolean }) =>
  api.post('/items/batch-export', data)

export const getAsset = (assetId: string) =>
  api.get<{ asset: Asset; versions: ProcessingVersion[] }>(`/assets/${assetId}`)
export const removeBackground = (assetId: string, engine_name?: string) =>
  api.post(`/assets/${assetId}/remove-background`, { engine_name })
export const saveMask = (assetId: string, blob: Blob, opts?: { as_new_version?: boolean; version_id?: string }) => {
  const form = new FormData()
  form.append('file', blob, 'mask.png')
  return api.post<ProcessingVersion>(`/assets/${assetId}/mask`, form, {
    params: {
      as_new_version: opts?.as_new_version || false,
      version_id: opts?.version_id,
    },
    headers: { 'Content-Type': 'multipart/form-data' },
  })
}
export const saveParams = (assetId: string, data: Record<string, unknown>) =>
  api.post<ProcessingVersion>(`/assets/${assetId}/params`, data)
export const markFinal = (versionId: string, is_final = true) =>
  api.post<ProcessingVersion>(`/assets/versions/${versionId}/final`, { is_final })
export const restoreAutoMask = (versionId: string) =>
  api.post<ProcessingVersion>(`/assets/versions/${versionId}/restore-auto-mask`)
export const renderVersion = (versionId: string) =>
  api.post<ProcessingVersion>(`/assets/versions/${versionId}/render`)
export const copyVersion = (versionId: string) =>
  api.post<ProcessingVersion>(`/assets/versions/${versionId}/copy`)
export const deleteVersion = (versionId: string) =>
  api.delete(`/assets/versions/${versionId}`)
export const exportAsset = (assetId: string, opts?: Record<string, unknown>) =>
  api.post(`/assets/${assetId}/export`, opts || {})
export const suggestAdjust = (assetId: string) =>
  api.get<{ auto_level: number; auto_wb: { temperature?: number; tint?: number } }>(`/assets/${assetId}/suggest`)

export const listJobs = (params?: { status?: string; workspace_id?: string }) =>
  api.get<Job[]>('/jobs', { params })
export const getJob = (id: string) => api.get<Job>(`/jobs/${id}`)
export const retryJob = (id: string) => api.post<Job>(`/jobs/${id}/retry`)
export const cancelJob = (id: string) => api.post<Job>(`/jobs/${id}/cancel`)

export const listEngines = () => api.get<EngineInfo[]>('/engines')
export const listCategories = () => api.get<LabelValue[]>('/meta/categories')
export const listShadowModes = () => api.get<LabelValue[]>('/meta/shadow-modes')

// ---------------------------------------------------------------------------
// Source-image pipeline: 01_原图 -> 02_透明底图 (no Item/Object entity).
// ---------------------------------------------------------------------------

export interface LibraryRootStatus {
  configured: boolean
  root_path: string | null
  dirs?: Record<string, string>
  originals_exists?: boolean
}

export const getLibraryRoot = () => api.get<LibraryRootStatus>('/library/root')
export const setLibraryRoot = (path: string) =>
  api.post<{ status: LibraryRootStatus; job: Job }>('/library/root', { path })
export const importSourceFolder = (path: string) =>
  api.post<{ job: Job }>('/library/import-source', { path })
export const rescanLibrary = () => api.post<{ job: Job }>('/library/scan')

export interface SourceImageOut {
  id: string
  relative_path: string
  folder_relative: string
  filename: string
  width: number | null
  height: number | null
  mime_type: string | null
  file_size: number | null
  status: string
  status_label: string
  error_message: string | null
  engine_name: string | null
  model_name: string | null
  model_version: string | null
  has_transparent: boolean
  has_unsaved_edits: boolean
  thumbnail_url: string | null
  proxy_url: string | null
  mask_url: string | null
  transparent_url: string | null
  transparent_thumb_url: string | null
  last_saved_at: string | null
  created_at: string
  updated_at: string
}

export const listSourceImages = (params?: { q?: string; folder?: string; status?: string; limit?: number }) =>
  api.get<SourceImageOut[]>('/images', { params })
export const listSourceFolders = () => api.get<string[]>('/folders')
export const getSourceImage = (id: string) => api.get<SourceImageOut>(`/images/${id}`)
export const cutoutSourceImage = (id: string, engine_name?: string) =>
  api.post<{ job: Job }>(`/images/${id}/cutout`, { engine_name })
export const batchCutoutSourceImages = (image_ids: string[], engine_name?: string) =>
  api.post<{ job: Job }>('/images/batch-cutout', { image_ids, engine_name })
export const saveManualMask = (imageId: string, blob: Blob) => {
  const form = new FormData()
  form.append('file', blob, 'mask.png')
  return api.post<SourceImageOut>(`/images/${imageId}/mask`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
}
export const saveTransparent = (imageId: string) => api.post<SourceImageOut>(`/images/${imageId}/save`)

export interface RenamePreview {
  preview: boolean
  ok: boolean
  conflicts: string[]
  moves: Array<{ kind: string; from: string; to: string; exists: boolean }>
}
export const renameSourceImage = (imageId: string, new_relative_path: string, confirm = false) =>
  api.post<RenamePreview | { preview: false; moved: string[]; new_relative_path: string }>(
    `/images/${imageId}/rename`,
    { new_relative_path, confirm },
  )
export const renameSourceFolder = (old_relative_dir: string, new_relative_dir: string, confirm = false) =>
  api.post<RenamePreview | { preview: false; moved: string[]; affected_images: number }>('/folders/rename', {
    old_relative_dir,
    new_relative_dir,
    confirm,
  })

// ---------------------------------------------------------------------------
// Fixed asset-root pipeline: 原图 → 透明底图 → 色底图 → 阴影图 (AssetChain)
// ---------------------------------------------------------------------------

export interface AssetRootCheck {
  kind: string
  path: string
  exists: boolean
  created: boolean
  readable: boolean
  writable: boolean
  free_mb: number | null
  error: string | null
}

export interface AssetRootsStatus {
  ok: boolean
  distinct_ok: boolean
  nesting_ok: boolean
  disk_ok: boolean
  errors: string[]
  roots: AssetRootCheck[]
}

export const getAssetRootsStatus = () => api.get<AssetRootsStatus>('/asset-roots/status')

// ---------------------------------------------------------------------------
// Browser Direct Upload (Phase 1A): Browser -> R2 presigned PUT -> Backend
// complete. Image bytes never pass through this backend (spec §14) — the
// PUT below goes straight to the presigned R2 URL, not `/api/*`.
// ---------------------------------------------------------------------------

export interface UploadConfig {
  storage_backend: 'local' | 'r2'
  /** false in Local mode — the Browser Upload entry point should stay
   * hidden behind the Native Folder Picker (spec §13/§19). */
  browser_upload_enabled: boolean
  max_size_mb: number
  allowed_mime: string[]
  max_concurrency: number
  /** VistoeraTASK-00522 central batch limits (absent on an older backend ->
   * the frontend falls back to the same documented defaults). */
  max_batch_files?: number
  max_batch_total_mb?: number
  client_concurrency?: number
  /** false (the default) -> the R2 bucket's CORS policy hasn't been
   * configured to allow this origin, so a real browser cannot PUT
   * directly to the presigned URL; use `uploadFileDirect` (proxy through
   * this backend) instead of `initiateUpload`+`putFileToPresignedUrl`. */
  direct_to_r2: boolean
}
export const getUploadConfig = () => api.get<UploadConfig>('/uploads/config')

export interface InitiateUploadResponse {
  upload_id: string
  storage_key: string
  upload_url: string
  expires_at: string
}
/** Upload options. `collection_id` / `visibility` are only sent by the team-mode
 * Image Asset Center (server validates them; omitted = 未整理图片 + 团队共享). */
export interface UploadPlacementOpts {
  auto_process?: boolean
  asset_source?: string | null
  collection_id?: string | null
  visibility?: 'workspace' | 'private' | null
  /** VistoeraTASK-00522 — client-allocated UUID reused by EVERY retry of one
   * file so a retry can never create a second asset (server: primary key). */
  upload_id?: string | null
}

/**
 * Per-request scope (VistoeraTASK-00522). The shared `api` client's baseURL is
 * a mutable module default that `ImageAssetCenterPage` resets on unmount; a
 * background upload that outlives the page must NOT follow that default, so the
 * upload manager pins the Workspace-scoped base URL (and credentials) on every
 * call. Omitted by every pre-existing caller (behaviour unchanged).
 */
export interface UploadRequestScope {
  baseURL?: string
  signal?: AbortSignal
}

const scopedConfig = (scope?: UploadRequestScope) =>
  scope
    ? {
        ...(scope.baseURL ? { baseURL: scope.baseURL, withCredentials: true } : {}),
        ...(scope.signal ? { signal: scope.signal } : {}),
      }
    : {}

export const initiateUpload = (
  filename: string,
  mime_type: string,
  size?: number,
  opts?: UploadPlacementOpts,
  scope?: UploadRequestScope,
) =>
  api.post<InitiateUploadResponse & { already_completed?: boolean }>(
    '/uploads/initiate',
    {
      filename,
      mime_type,
      size,
      auto_process: opts?.auto_process ?? true,
      asset_source: opts?.asset_source ?? null,
      ...(opts?.collection_id ? { collection_id: opts.collection_id } : {}),
      ...(opts?.visibility ? { visibility: opts.visibility } : {}),
      ...(opts?.upload_id ? { upload_id: opts.upload_id } : {}),
    },
    scopedConfig(scope),
  )

export const retryUploadPresign = (uploadId: string, scope?: UploadRequestScope) =>
  api.post<{ upload_id: string; storage_key: string; upload_url: string }>(
    `/uploads/${uploadId}/retry`,
    undefined,
    scopedConfig(scope),
  )

export const completeUpload = (uploadId: string, scope?: UploadRequestScope) =>
  api.post<AssetChainOut>(`/uploads/${uploadId}/complete`, undefined, scopedConfig(scope))

/** The one call in this file that never goes to this backend at all — a
 * direct PUT of the raw file bytes to the presigned R2 URL from
 * `initiateUpload`/`retryUploadPresign` (spec §14/§15). Uses a bare axios
 * instance (no `/api` baseURL, no shared error-message interceptor) since
 * the target host and error shape are R2's, not ours. */
export const putFileToPresignedUrl = (
  uploadUrl: string,
  file: File,
  onProgress?: (pct: number) => void,
  signal?: AbortSignal,
) =>
  axios.put(uploadUrl, file, {
    headers: { 'Content-Type': file.type || 'application/octet-stream' },
    timeout: 0,
    ...(signal ? { signal } : {}),
    onUploadProgress: (evt) => {
      if (onProgress && evt.total) onProgress(Math.round((evt.loaded / evt.total) * 100))
    },
  })

/** Proxy transport used whenever `UploadConfig.direct_to_r2` is false (the
 * default) — bytes go through this backend's own `/uploads/direct` (same
 * origin as every other call in this file, so no R2 CORS is involved at
 * all). Functionally replaces `initiateUpload` + `putFileToPresignedUrl` +
 * `completeUpload` with a single round trip. */
export const uploadFileDirect = (
  file: File,
  onProgress?: (pct: number) => void,
  opts?: UploadPlacementOpts,
  scope?: UploadRequestScope,
) => {
  const form = new FormData()
  form.append('file', file)
  form.append('auto_process', String(opts?.auto_process ?? true))
  if (opts?.asset_source) form.append('asset_source', opts.asset_source)
  if (opts?.collection_id) form.append('collection_id', opts.collection_id)
  if (opts?.visibility) form.append('visibility', opts.visibility)
  if (opts?.upload_id) form.append('upload_id', opts.upload_id)
  return api.post<AssetChainOut>('/uploads/direct', form, {
    ...scopedConfig(scope),
    timeout: 0,
    onUploadProgress: (evt) => {
      if (onProgress && evt.total) onProgress(Math.round((evt.loaded / evt.total) * 100))
    },
  })
}

export type StageStatus =
  | 'waiting'
  | 'queued'
  | 'processing'
  | 'done'
  | 'failed'
  | 'upstream_missing'
  | 'not_applicable'

export interface AssetChainOut {
  id: string
  group_id: string | null
  group_relative_path: string
  original_relative_path: string
  original_filename: string
  original_width: number | null
  original_height: number | null
  original_missing: boolean
  transparent_relative_path: string | null
  background_relative_path: string | null
  shadow_relative_path: string | null
  transparent_status: StageStatus
  transparent_status_label: string
  background_status: StageStatus
  background_status_label: string
  shadow_status: StageStatus
  shadow_status_label: string
  current_stage: string | null
  current_progress: number
  current_message: string | null
  current_matting_engine: string | null
  last_error_stage: string | null
  last_error_message: string | null
  thumbnail_url: string | null
  proxy_url: string | null
  mask_url: string | null
  transparent_url: string | null
  /** Viewport display URL for transparent (~2200px alpha-preserving proxy).
   * Object Image Viewer UX P0 — open the contained viewer on this, not
   * `transparent_url` (full PNG). */
  transparent_proxy_url?: string | null
  background_url: string | null
  shadow_url: string | null
  original_url: string | null
  /** Image Asset Loading Performance P0 (2026-08) — grid-cell-sized
   * sibling of `transparent_url`/`background_url`/`shadow_url` above; the
   * shared `ChainRow`'s small grid cells load these instead of the full-
   * resolution URLs. Viewer open uses `transparent_proxy_url` (not full). */
  transparent_thumb_url: string | null
  background_thumb_url: string | null
  shadow_thumb_url: string | null
  /** Single-asset download links (Image Assets UX release, spec 一.1) —
   * force `Content-Disposition: attachment`, distinct from the `*_url`
   * view/lightbox links above. `null` when that stage hasn't been
   * generated yet (only the SaaS-scoped `/images/chains` serializer sets
   * these; the standalone `/chains` list leaves them `undefined`, so
   * `Home.tsx` simply never renders a download button). */
  original_download_url?: string | null
  transparent_download_url?: string | null
  background_download_url?: string | null
  shadow_download_url?: string | null
  background_params: BackgroundEditorParams | null
  shadow_params: ShadowEditorParams | null
  /** Best-effort "currently effective cutout config" (engine/model/
   * MaskPostParams) — spec 十二 currentProcessingConfig.cutout. Populated by
   * the automatic pipeline and by the single-image editor's auto-cutout
   * step; falls back to the bound template's frozen cutout snapshot for
   * chains processed before this field existed. */
  cutout_params: Record<string, unknown> | null
  process_template_id: string | null
  process_template_name: string | null
  process_template_version: number | null
  /** The template originally used the very first time this chain was
   * auto-processed — stamped once, never overwritten (spec 十二
   * originalTemplateId). Powers "重跑全部流程 → 原模板". */
  original_template_id: string | null
  /** ASSET-UX-CONSISTENCY-A1 — the authoritative revision counters for
   * `background_url`/`shadow_url` (same static storage-key-derived path on
   * every request; these are what actually change on a genuinely new
   * artifact — auto pipeline run OR manual Editor save). Use these, not
   * `updated_at`, as the cache-busting token for those two URLs — see
   * `bustBackgroundUrl`/`bustShadowUrl` in `StageFlow.tsx`. */
  background_generation: number
  shadow_generation: number
  /** Image Asset Loading Performance P0 (2026-08) — the authoritative
   * revision counter for `transparent_url`/`transparent_thumb_url`, same
   * role as `background_generation`/`shadow_generation` above. Use this
   * as their cache-busting token, NOT `updated_at` (which changes on
   * every save to this chain, including an unrelated background/shadow
   * save, and would otherwise bust/re-download the unchanged transparent
   * thumbnail on every one of those). */
  transparent_generation: number
  created_at: string | null
  updated_at: string | null
  /** Image Asset Center — provenance of the original bytes. */
  asset_source?: string | null
  original_size_bytes?: number | null
  auto_process_enabled?: boolean
}

/** The three concepts spec 十二 asks to keep distinct, mapped onto the
 * fields actually available on `AssetChainOut`:
 *   - originalTemplateId  -> chain.original_template_id
 *   - currentTemplateId   -> chain.process_template_id
 *   - currentProcessingConfig -> { cutout, background, shadow }_params below
 */
export function currentProcessingConfigOf(chain: AssetChainOut): AutoProcessTemplateParams {
  return {
    schemaVersion: 1,
    cutout: (chain.cutout_params as AutoProcessTemplateParams['cutout']) || { engine: 'rembg', parameters: {} },
    background: chain.background_params || ({} as BackgroundEditorParams),
    shadow: chain.shadow_params || ({} as ShadowEditorParams),
  }
}

/** Sentinel accepted by the rerun endpoints in place of a real template id —
 * "恢复原模板参数" (spec 二/十二). Never shown to the user as raw text; the
 * UI always renders it as a labeled radio option. */
export const ORIGINAL_TEMPLATE_SENTINEL = 'original'

export interface AutoProcessTemplateParams {
  schemaVersion: number
  cutout: {
    engine: string
    model?: string
    parameters: Record<string, unknown>
  }
  background: BackgroundEditorParams
  shadow: ShadowEditorParams
}

export type TemplateStageStatus = 'idle' | 'done' | 'failed' | 'stale'

export interface AutoProcessTemplate {
  id: string
  name: string
  description: string | null
  is_default: boolean
  is_system: boolean
  schema_version: number
  template_version: number
  params: AutoProcessTemplateParams
  preview_source_url: string | null
  preview_transparent_url: string | null
  preview_background_url: string | null
  preview_shadow_url: string | null
  cutout_status: TemplateStageStatus
  cutout_status_label: string
  cutout_error: string | null
  background_status: TemplateStageStatus
  background_status_label: string
  background_error: string | null
  shadow_status: TemplateStageStatus
  shadow_status_label: string
  shadow_error: string | null
  is_active: boolean
  created_at: string | null
  updated_at: string | null
}

export interface AutoProcessTemplateList {
  templates: AutoProcessTemplate[]
  active_id: string | null
}

/** Background Editor (色底图编辑器) saved state — canvas ratio, background
 * fill, and the transparent subject's placement. Persisted so the editor
 * (and the downstream Shadow Editor, which must keep the exact same layout)
 * can be reopened without resetting to defaults. */
export interface BackgroundEditorParams {
  ratioKey: string
  bgPreset: string
  bgCustomColor: string
  /** Composable model (current) — radial/vertical/horizontal axes that can
   * be independently enabled and combined (spec 三.1). Always present once
   * saved by the current editor; read through `migrateBackgroundGradients()`
   * before use so legacy params (below) are transparently upgraded. */
  gradients?: BackgroundGradientConfig
  /** @deprecated legacy (pre-composable) exclusive mode — 'solid' | 'radial'
   * | 'vertical' | 'horizontal'. Only present on params saved before the
   * composable model existed; never written by the current editor. */
  bgMode?: string
  /** @deprecated legacy single-mode signed -100..100 strength paired with
   * `bgMode` above (0 = solid, no gradient) when `gradientVersion === 2`.
   * Even older persisted params lack `gradientVersion` and stored this as a
   * one-directional 0-100 value. Never written by the current editor. */
  gradientIntensity?: number
  /** @deprecated present + `=== 2` on params saved by the old single-mode
   * editor. Missing on both the current composable model and the oldest
   * (pre-v2) legacy params. */
  gradientVersion?: number
  transform: { scalePct: number; offsetXFrac: number; offsetYFrac: number }
}

/** Shadow Editor (灯光与落地阴影编辑器) saved state — a single ground-
 * projection shadow driven by light direction + light height (see
 * `ShadowEditorModal.tsx` / `shadowRender.ts`). The backend stores this
 * verbatim as opaque JSON, so this type only needs to stay in sync with the
 * frontend's own `ShadowParams` shape. */
export interface ShadowEditorParams {
  version: number
  /** Stable id of the (frontend-local) `SavedShadowPreset` this snapshot's
   * params were last loaded from/saved against, if any — informational
   * only (see `ShadowParams.sourcePresetId` in `shadowRender.ts`). A normal
   * save always writes this reference verbatim; it never creates or
   * renames a global preset. */
  sourcePresetId?: string | null
  /** @deprecated legacy field some old saves used to rename this snapshot
   * to a preset's display name; no longer written. Kept optional only so
   * old persisted JSON still type-checks when read back. */
  presetName?: string
  light: { azimuthDeg: number; heightPct: number }
  shadow: {
    length: number
    softness: number
    strength: number
    farFade: number
    taper: number
  }
  groundBaselineOffsetFrac: number
}

export interface AssetGroupOut {
  id: string
  relative_folder_path: string
  display_name: string
  image_count: number
  done_count: number
  processing_count: number
  failed_count: number
}

export interface ChainListSummary {
  total: number
  done: number
  processing: number
  failed: number
  auto_process_paused: boolean
}

export interface ChainListResponse {
  chains: AssetChainOut[]
  groups: AssetGroupOut[]
  summary: ChainListSummary
  /** Only present from the paginated `/images/chains` (SaaS) list —
   * `undefined` on the standalone `/chains` route, which still returns
   * everything up to `limit` in one page (spec 一.6 only covers the SaaS
   * Images page). */
  page?: number
  page_size?: number
  total_pages?: number
}

export const listChains = (params?: {
  q?: string
  group_id?: string
  status?: string
  limit?: number
  page?: number
  page_size?: number
}) => api.get<ChainListResponse>('/chains', { params })
export const getChain = (id: string) => api.get<AssetChainOut>(`/chains/${id}`)

// ---------------------------------------------------------------------------
// Image Asset Center — virtual library projection over chain stages
// ---------------------------------------------------------------------------

export type LibraryAssetType =
  | 'original'
  | 'transparent'
  | 'color'
  | 'shadow'
  | 'flat_rectified'
  | 'flat_enhanced'
  | 'ai_generated'

export interface LibraryAssetItem {
  id: string
  picker_item_id?: string
  reference_type?: 'site_asset' | 'library_item'
  chain_id: string | null
  asset_type: LibraryAssetType | string
  asset_type_label: string
  /** VistoeraTASK-00557 - first-level classification decided by the server. */
  origin?: ImageOrigin
  origin_label?: string
  /** Edit method of an edit_generated image (transparent, color, shadow, crop, flat_rectified, flat_enhanced, edit). */
  origin_detail?: string | null
  resource_type?: string
  resource_type_label?: string
  source: string
  source_label: string
  canonical_source?: string
  canonical_source_label?: string
  filename: string
  width: number | null
  height: number | null
  size_bytes: number | null
  preview_url: string | null
  /** Tier-2 viewport display URL (proxy / transparent_proxy). */
  display_url?: string | null
  download_url: string | null
  full_url: string | null
  created_at: string | null
  updated_at: string | null
  processing_status: string | null
  metadata: Record<string, unknown>
  lineage: Record<string, unknown>
  lineage_siblings?: Array<{
    id: string
    asset_type: string
    asset_type_label: string
    filename: string
    preview_url: string | null
  }>
  variants?: Array<{
    id?: string
    filename?: string
    resource_type_label?: string
    thumbnail_url?: string
  }>
  usage?: unknown
  /** Team sharing (VistoeraTASK-00508) - canonical attributes, always present. */
  created_by_user_id?: string | null
  visibility?: ImageVisibility
  collection_id?: string | null
  /** Team mode only: real creator profile (null = unknown creator). */
  creator?: ImageCreator | null
  /** Team mode only: server-computed permission to share/move/delete this image. */
  can_manage?: boolean
}

/** VistoeraTASK-00557 - 上传图 / AI生成 / 编辑生成 (derived, never client-supplied). */
export type ImageOrigin = 'upload' | 'ai_generated' | 'edit_generated'
export type ImageOriginCounts = { all: number } & Record<ImageOrigin, number>

export type ImageVisibility = 'workspace' | 'private'
export type ImageLibraryView = 'mine' | 'shared' | 'all'

export interface ImageCreator {
  user_id: string
  display_name: string
  is_me: boolean
}

export interface ImageTeamMember {
  user_id: string
  display_name: string
  role: string
}

/** `team.enabled === false` => personal Workspace: nothing team-related is shown. */
export interface ImageTeamBlock {
  enabled: boolean
  /** Active WorkspaceMemberships of this Workspace (diagnostic; team mode iff >= 2). */
  active_member_count?: number
  view?: ImageLibraryView
  user_id?: string
  role?: string
  can_manage_collections?: boolean
  unsorted_collection_id?: string | null
  /** @deprecated VistoeraTASK-00580 — use creator_options (visible-asset creators). */
  members?: ImageTeamMember[]
  /** Creators of assets visible in the active view (shared|all), not the roster. */
  creator_options?: Array<{ user_id: string; display_name: string; is_me?: boolean }>
}

export interface LibraryListResponse {
  items: LibraryAssetItem[]
  total: number
  page: number
  page_size: number
  summary: Record<string, number>
  /** Four first-level card counts over the same scoped feed as `total`. */
  origins?: ImageOriginCounts | null
  available_sources: Array<{ value: string; label: string; count: number }>
  asset_types: Array<{ value: string; label: string; count: number }>
  team?: ImageTeamBlock
}

export const listImageLibrary = (params?: {
  q?: string
  asset_type?: string
  /** VistoeraTASK-00557 first-level filter. */
  origin?: ImageOrigin
  source?: string
  processing_status?: string
  page?: number
  page_size?: number
  sort?: 'recent' | 'oldest' | 'name'
  /** Team mode only (ignored by the server for personal Workspaces). */
  view?: ImageLibraryView
  creator_id?: string
  collection_id?: string
}, signal?: AbortSignal) => api.get<LibraryListResponse>('/library', { params, signal })

// ---- Team sharing + Collections (VistoeraTASK-00508) -----------------------

export interface ImageCollection {
  id: string
  name: string
  is_system: boolean
  system_key: string | null
  asset_domain: string
  count: number
  created_by: { user_id: string; display_name: string } | null
  created_at: string | null
  /** VistoeraTASK-00523: unlimited-depth tree (same Workspace). null = root level. */
  workspace_id?: string
  parent_id?: string | null
  child_count?: number
  /** Subtree assets the CURRENT user may see (never leaks private counts). */
  accessible_asset_count?: number
}

export interface ImageCollectionsResponse {
  collections: ImageCollection[]
  unsorted_collection_id: string | null
  can_manage: boolean
  view: ImageLibraryView
}

export const listImageCollections = (view: ImageLibraryView, signal?: AbortSignal) =>
  api.get<ImageCollectionsResponse>('/collections', { params: { view }, signal })
export const createImageCollection = (name: string, parentId?: string | null) =>
  api.post<ImageCollection>('/collections', parentId ? { name, parent_id: parentId } : { name })
export const renameImageCollection = (id: string, name: string) =>
  api.patch<ImageCollection>(`/collections/${id}`, { name })
export const deleteImageCollection = (id: string) => api.delete<{ deleted: boolean }>(`/collections/${id}`)

export interface TeamBatchResult {
  updated: string[]
  rejected: Array<{ picker_item_id: string; reason: string }>
}
export const setImageVisibility = (pickerItemIds: string[], visibility: ImageVisibility) =>
  api.post<TeamBatchResult>('/team/visibility', { picker_item_ids: pickerItemIds, visibility })
export const moveImagesToCollection = (pickerItemIds: string[], collectionId: string | null) =>
  api.post<TeamBatchResult>('/team/move', { picker_item_ids: pickerItemIds, collection_id: collectionId })

export const getImageLibrarySummary = () =>
  api.get<{ summary: Record<string, number>; available_sources: LibraryListResponse['available_sources'] }>(
    '/library/summary',
  )

export const getImageLibraryItem = (itemId: string) => api.get<LibraryAssetItem>(`/library/${itemId}`)

export const startChainProcessing = (chainId: string) =>
  api.post<AssetChainOut>(`/chains/${chainId}/start-processing`)

/** VistoeraTASK-00624 — Entry C: submit picker item ids into object processing. */
export const submitChainsFromPicker = (pickerItemIds: string[]) =>
  api.post<{ chains: AssetChainOut[]; count: number }>('/chains/submit-from-picker', {
    picker_item_ids: pickerItemIds,
  })

/**
 * Triggers a native browser "Save As" for an already-absolute, cookie-
 * authenticated URL (a single-asset `*_download_url`, or a blob: URL built
 * from a batch ZIP response below) without navigating the SPA away from
 * itself. The real filename/MIME comes from the response's own
 * `Content-Disposition`/`Content-Type` headers (set server-side — see
 * `app/utils/content_disposition.py`), never from this helper, so it works
 * identically whether `url` is same-origin, a different origin (the API),
 * or a `blob:` URL.
 */
export function triggerBrowserDownload(url: string, filename?: string): void {
  const a = document.createElement('a')
  a.href = url
  // Only actually honored by browsers for a same-origin URL (notably a
  // `blob:` URL, which has no `Content-Disposition` of its own — see the
  // batch-download blob path below); ignored for the cross-origin API URL
  // a single-asset download uses, where the server's own
  // `Content-Disposition: attachment` header already supplies the name.
  if (filename) a.download = filename
  a.rel = 'noopener'
  document.body.appendChild(a)
  a.click()
  a.remove()
}

export function imageAssetAttachmentUrl(url: string): string {
  if (!url) return url
  if (url.includes('download=true') || url.includes('/download?')) return url
  if (url.includes('/variants/')) {
    return `${url}${url.includes('?') ? '&' : '?'}download=true`
  }
  return url
}

export function triggerImageAssetDownload(url: string): void {
  triggerBrowserDownload(imageAssetAttachmentUrl(url))
}

const DEFAULT_BATCH_DOWNLOAD_GAP_MS = 220

function sleepMs(ms: number, signal?: AbortSignal): Promise<void> {
  if (signal?.aborted) return Promise.reject(new DOMException('Aborted', 'AbortError'))
  return new Promise((resolve, reject) => {
    const id = window.setTimeout(() => {
      signal?.removeEventListener('abort', onAbort)
      resolve()
    }, ms)
    const onAbort = () => {
      window.clearTimeout(id)
      reject(new DOMException('Aborted', 'AbortError'))
    }
    signal?.addEventListener('abort', onAbort, { once: true })
  })
}

/**
 * VistoeraTASK-00727 — fire N attachment downloads in one user gesture.
 *
 * Root cause of "N selected → only 1 saved" (TASK-00725/00726 sync multi
 * ``a.click()``): Image Asset download URLs are cross-origin (app → API /
 * CDN). Browsers ignore ``download`` on cross-origin anchors, so each
 * ``a.click()`` is a top-level attachment *navigation*. Rapid successive
 * top-level navigations cancel earlier ones — only the last request
 * typically survives. Calling ``a.click()`` N times ≠ N network downloads.
 *
 * Fix: one hidden iframe per URL. Each iframe owns an isolated browsing
 * context, so attachment navigations do not cancel siblings. Still sync
 * (no setTimeout between starts); no ZIP; no blob fetch of originals.
 * Caller enforces product limit (Asset Center: max 10). Return value only
 * counts triggers attempted; browsers do not expose disk-save success.
 *
 * Compatibility: Chromium / Firefox / Safari all start downloads from
 * iframe ``src`` when the response has ``Content-Disposition: attachment``
 * (our media download path). Chrome may still prompt once for "automatic
 * downloads" after the first file — that is a real browser permission,
 * not a Vistoera false toast.
 */
export function triggerImageAssetDownloadsNative(urls: string[]): {
  triggered: number
  skipped: number
} {
  const valid = urls.filter((u) => Boolean(u?.trim())).map((u) => imageAssetAttachmentUrl(u))
  if (valid.length === 0) {
    return { triggered: 0, skipped: urls.length }
  }
  // Single file: keep the battle-tested anchor path (one navigation, no cancel).
  if (valid.length === 1) {
    triggerBrowserDownload(valid[0])
    return { triggered: 1, skipped: urls.length - 1 }
  }
  const frames: HTMLIFrameElement[] = []
  for (const href of valid) {
    const iframe = document.createElement('iframe')
    iframe.setAttribute('aria-hidden', 'true')
    iframe.tabIndex = -1
    iframe.style.display = 'none'
    iframe.style.width = '0'
    iframe.style.height = '0'
    iframe.style.border = '0'
    iframe.src = href
    document.body.appendChild(iframe)
    frames.push(iframe)
  }
  // Detach after the browser has had time to start each download. Removing
  // immediately can abort in-flight attachment navigations; delaying the
  // *start* of clicks is intentionally avoided (would drop user activation).
  window.setTimeout(() => {
    for (const frame of frames) {
      frame.remove()
    }
  }, 60_000)
  return { triggered: valid.length, skipped: urls.length - valid.length }
}

/** @deprecated Prefer ``triggerImageAssetDownloadsNative`` (TASK-00725). */
export async function triggerImageAssetDownloadsSequential(
  urls: string[],
  options?: {
    delayMs?: number
    signal?: AbortSignal
    onProgress?: (triggered: number, total: number) => void
  },
): Promise<{ triggered: number; skipped: number }> {
  const delayMs = options?.delayMs ?? DEFAULT_BATCH_DOWNLOAD_GAP_MS
  const valid = urls.filter((u) => Boolean(u?.trim()))
  const skipped = urls.length - valid.length
  let triggered = 0
  for (let i = 0; i < valid.length; i += 1) {
    if (options?.signal?.aborted) break
    triggerImageAssetDownload(valid[i])
    triggered += 1
    options?.onProgress?.(triggered, valid.length)
    if (i < valid.length - 1) {
      try {
        await sleepMs(delayMs, options?.signal)
      } catch {
        break
      }
    }
  }
  return { triggered, skipped }
}

/**
 * Batch download (spec 一.2) — POSTs the selected chain ids and streams
 * back a ZIP built server-side (`app/services/chain_batch_download.py`).
 * Fetched as a blob (not a plain link) because the request body must carry
 * potentially many ids, which a GET/query-string link cannot do safely.
 * Used by the legacy ImagesPage pipeline UI — not Image Asset Center.
 */
export const downloadChainsBatch = (chainIds: string[]) =>
  api.post<Blob>('/chains/batch-download', { chain_ids: chainIds }, { responseType: 'blob' })

export const planImport = (external_path: string) =>
  api.post<{
    loose_images: number
    groups: Record<string, number>
    ignored_non_image: number
    conflicts: string[]
    total_images: number
  }>('/import/plan', { external_path })
export const runImport = (external_path: string, conflict_policy: 'skip' | 'overwrite' | 'cancel' = 'skip') =>
  api.post<{ job_id: string }>('/import/run', { external_path, conflict_policy })

export const retryChainStage = (id: string, stage: 'transparent' | 'background' | 'shadow') =>
  api.post(`/chains/${id}/retry`, null, { params: { stage } })

/**
 * "重跑全部流程" (spec 二/三/十二) — the ONE function single-image rerun
 * calls. `templateId` is either a real `AutoProcessTemplate.id` (from the
 * same list the top template bar shows) or `ORIGINAL_TEMPLATE_SENTINEL`
 * ("原模板" — restore this chain's own `original_template_id`). No more
 * "模板快照" concept/option anywhere in the UI.
 */
export const rerunWithTemplate = (chainId: string, templateId: string) =>
  api.post<{ ok: boolean; process_template_id: string | null; process_template_name: string | null }>(
    `/chains/${chainId}/reprocess`,
    { template_id: templateId },
  )

/** Group equivalent — shares the same backend resolution logic per-chain,
 * so `ORIGINAL_TEMPLATE_SENTINEL` restores each chain in the group to its
 * own original template rather than one shared template. */
export const rerunGroupWithTemplate = (groupId: string, templateId: string) =>
  api.post<{ ok: boolean; count: number }>(`/groups/${groupId}/reprocess`, { template_id: templateId })

/**
 * Batch equivalent used by multi-select "批量处理" (spec 四) — deliberately
 * the *same* request shape/backend implementation as `rerunWithTemplate`,
 * just addressing many chains at once, so the two flows can never drift out
 * of sync (spec 十五).
 */
export const batchRerunWithTemplate = (chainIds: string[], templateId: string) =>
  api.post<{ ok: boolean; count: number }>('/chains/batch-rerun', { chain_ids: chainIds, template_id: templateId })

export const deleteChainOriginal = (id: string, cascade: boolean) =>
  api.delete(`/chains/${id}/original`, { data: { cascade } })
export const deleteChainTransparent = (id: string, cascade_downstream: boolean) =>
  api.delete(`/chains/${id}/transparent`, { data: { cascade_downstream } })
export const deleteChainBackground = (id: string, cascade_shadow: boolean) =>
  api.delete(`/chains/${id}/background`, { data: { cascade_shadow } })
export const deleteChainShadow = (id: string) => api.delete(`/chains/${id}/shadow`)
export const deleteChain = (id: string) => api.delete(`/chains/${id}`)
export const renameChain = (id: string, new_name: string) =>
  api.post<AssetChainOut>(`/chains/${id}/rename`, { new_name })

export const revealChain = (id: string) => api.post<{ ok: boolean }>(`/chains/${id}/reveal`)
export const revealGroup = (id: string) => api.post<{ ok: boolean }>(`/groups/${id}/reveal`)

export const deleteGroup = (id: string) => api.delete(`/groups/${id}`)
export const renameGroup = (id: string, new_name: string) =>
  api.post<{ id: string; relative_folder_path: string; display_name: string }>(`/groups/${id}/rename`, { new_name })

export const listAutoProcessTemplates = () =>
  api.get<AutoProcessTemplateList>('/auto-process-templates')
export const getActiveAutoProcessTemplate = () =>
  api.get<AutoProcessTemplate>('/auto-process-templates/active')
export const createAutoProcessTemplate = (body: {
  name: string
  description?: string
  params?: AutoProcessTemplateParams
  set_active?: boolean
  copy_from_id?: string
}) => api.post<AutoProcessTemplate>('/auto-process-templates', body)
export const updateAutoProcessTemplate = (
  id: string,
  body: { name?: string; description?: string; params?: AutoProcessTemplateParams },
) => api.put<AutoProcessTemplate>(`/auto-process-templates/${id}`, body)
export const deleteAutoProcessTemplate = (id: string) => api.delete(`/auto-process-templates/${id}`)
export const duplicateAutoProcessTemplate = (id: string) =>
  api.post<AutoProcessTemplate>(`/auto-process-templates/${id}/duplicate`)
export const setActiveAutoProcessTemplate = (id: string) =>
  api.post<AutoProcessTemplate>(`/auto-process-templates/${id}/set-active`)
export const setDefaultAutoProcessTemplate = (id: string) =>
  api.post<AutoProcessTemplate>(`/auto-process-templates/${id}/set-default`)
export const uploadTemplatePreviewSource = (id: string, file: File) => {
  const form = new FormData()
  form.append('file', file)
  return api.post<AutoProcessTemplate>(`/auto-process-templates/${id}/preview-source`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
}
export const deleteTemplatePreviewSource = (id: string) =>
  api.delete<AutoProcessTemplate>(`/auto-process-templates/${id}/preview-source`)

/** Template debug pipeline (spec: "自动处理模板" 展开后必须展示真实处理结果，
 * 不是抽象参数表单) — runs the real cutout/background/shadow synthesis
 * against the template's own test image (`preview_source_url`). */
export const runTemplateCutout = (
  id: string,
  body: { engine?: string; model?: string; parameters?: Record<string, unknown>; cascade?: boolean } = {},
) => api.post<AutoProcessTemplate>(`/auto-process-templates/${id}/preview/cutout`, body)

export const runTemplateBackground = (id: string, overrides?: Record<string, unknown>) =>
  api.post<AutoProcessTemplate>(`/auto-process-templates/${id}/preview/background/run`, { overrides })

export const saveTemplateBackground = (id: string, blob: Blob, params: BackgroundEditorParams) => {
  const form = new FormData()
  form.append('file', blob, 'background.jpg')
  form.append('params', JSON.stringify(params))
  return api.post<AutoProcessTemplate>(`/auto-process-templates/${id}/preview/background/save`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
}

export const runTemplateShadow = (id: string, overrides?: Record<string, unknown>) =>
  api.post<AutoProcessTemplate>(`/auto-process-templates/${id}/preview/shadow/run`, { overrides })

export const saveTemplateShadow = (id: string, blob: Blob, params: ShadowEditorParams) => {
  const form = new FormData()
  form.append('file', blob, 'shadow.jpg')
  form.append('params', JSON.stringify(params))
  return api.post<AutoProcessTemplate>(`/auto-process-templates/${id}/preview/shadow/save`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
}

export const runTemplateAll = (id: string) =>
  api.post<AutoProcessTemplate>(`/auto-process-templates/${id}/preview/run-all`)

/**
 * "保存当前图片并更新模板" (spec 十) — syncs one stage's just-saved params
 * from a *real asset* edit into the currently active auto-process template,
 * so later uploads reuse this tuning. Only the named stage is replaced; the
 * template's other two stages are left untouched. This never touches the
 * asset itself (already saved separately) and never touches previously
 * completed chains — it only changes what *future* uploads will use.
 */
export const syncStageToActiveTemplate = async (
  stage: 'cutout' | 'background' | 'shadow',
  stageParams: Record<string, unknown>,
): Promise<AutoProcessTemplate> => {
  const { data: active } = await getActiveAutoProcessTemplate()
  const merged: AutoProcessTemplateParams = { ...active.params, [stage]: stageParams } as AutoProcessTemplateParams
  const { data } = await updateAutoProcessTemplate(active.id, { params: merged })
  return data
}

export const reconcileAssetRoots = () => api.post('/reconcile')
export const getLastReconcileReport = () => api.get('/reconcile/last')

export const getAutoProcessStatus = () => api.get<{ paused: boolean }>('/auto-process/status')
export const pauseAutoProcess = () => api.post<{ paused: boolean }>('/auto-process/pause')
export const resumeAutoProcess = () => api.post<{ paused: boolean }>('/auto-process/resume')

export const editorOpen = (chainId: string) =>
  api.post<AssetChainOut & { loaded_existing_mask: boolean }>(`/chains/${chainId}/editor/open`)
export const editorAutoCutout = (chainId: string, engine_name?: string) =>
  api.post<AssetChainOut>(`/chains/${chainId}/editor/auto-cutout`, { engine_name })
export const editorSaveMask = (chainId: string, blob: Blob) => {
  const form = new FormData()
  form.append('file', blob, 'mask.png')
  return api.post<AssetChainOut>(`/chains/${chainId}/editor/mask`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
}

/** TASK-00614: persist browser-composited RGBA PNG (no server-side original compose). */
export const editorSave = (chainId: string, transparentPng: Blob) => {
  const form = new FormData()
  form.append('file', transparentPng, 'transparent.png')
  return api.post<AssetChainOut>(`/chains/${chainId}/editor/save`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
}

/**
 * TASK-00611/00614: mask upload + client-composited transparent PNG upload.
 * Server no longer materializes the original for editor save (Preview 512MB
 * OOM). Retry overwrites the same chain keys — no duplicate assets.
 */
export async function editorSaveTransparentWithRetry(
  chainId: string,
  maskBlob: Blob,
  transparentPng: Blob,
): Promise<AssetChainOut> {
  await withTransientRetry(() => editorSaveMask(chainId, maskBlob), 2)
  const { data } = await withTransientRetry(() => editorSave(chainId, transparentPng), 2)
  return data
}

/** Background Editor (色底图编辑器): the final composite (ratio + background
 * + positioned subject) is rendered entirely client-side on a canvas; this
 * just uploads the flattened result to overwrite the chain's single 色底图.
 * ``params`` is stored alongside so the editor (and the Shadow Editor) can
 * restore the exact same canvas/subject layout later. */
export const backgroundEditorSave = (chainId: string, blob: Blob, params: BackgroundEditorParams) => {
  const form = new FormData()
  form.append('file', blob, 'background.jpg')
  form.append('params', JSON.stringify(params))
  return api.post<AssetChainOut>(`/chains/${chainId}/editor/background/save`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
}

/** Shadow Editor (灯光与落地阴影编辑器): background + multi-layer shadow +
 * subject are all rendered client-side on a canvas; this uploads the
 * flattened result to overwrite the chain's single 阴影图, together with the
 * lighting/shadow parameters so the editor can be reopened later. */
export const shadowEditorSave = (chainId: string, blob: Blob, params: ShadowEditorParams) => {
  const form = new FormData()
  form.append('file', blob, 'shadow.jpg')
  form.append('params', JSON.stringify(params))
  return api.post<AssetChainOut>(`/chains/${chainId}/editor/shadow/save`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
}

export default api
